import { enqueueSnackbar } from 'notistack';

// Verifica se o objeto Android está disponível no escopo global
// eslint-disable-next-line prefer-destructuring
// obtém a interface exposta pelo WebView
const android = typeof window !== 'undefined' && window.Android;

if (!android) {
  console.warn('O objeto Android não está disponível.');
}

// ******************** FUNÇÕES **********************

/**
 * Envia uma notificação ao Android.
 * @param {Object} message - Objeto de mensagem contendo 'from' e 'valor'.
 */
export function androidNotification(from, valor) {
  if (!android || typeof android.showNotification !== 'function') {
    return;
  }

  const payload = JSON.stringify({ from, valor });
  try {
    android.showNotification(payload);
  } catch (err) {
    console.error('Erro ao chamar Android.showNotification:', err);
  }
}

/**
 * Retorna a versão atual do App Android.
 * @returns {string} - v25050818
 */
export function androidTecnozapVersion() {
  if (!android) {
    return '';
  }
  try {
    return android.versionNameApp();
  } catch (err) {
    enqueueSnackbar(`Erro ao verificar versão: ${err}`, { variant: 'error' });
  }
  return '';
}

/**
 * Obtém o frame de um arquivo.
 * @param {number} fileIndex - Índice do arquivo.
 * @returns {string|undefined} - Frame convertido (base64) ou undefined em caso de erro.
 */
export function androidGetFrame(fileIndex) {
  if (!android) {
    enqueueSnackbar('Android não disponível para obter frame.', {
      variant: 'warning',
    });
    return undefined; // Retorna undefined explicitamente
  }
  try {
    const frame = android.getFrame(fileIndex);
    console.log('Frame obtido:', frame);
    return frame; // Retorna o frame obtido
  } catch (err) {
    console.error('Erro ao obter frame:', err);
    enqueueSnackbar(`Erro ao obter frame: ${err}`, { variant: 'error' });
  }
  return undefined; // Retorna undefined caso ocorra uma exceção
}

/**
 * Abre um arquivo no Android.
 * @param {Object} file - Objeto contendo informações do arquivo.
 * @param {number} file.file - Índice do arquivo.
 */
export function androidShowFile(file) {
  if (!android) {
    enqueueSnackbar('Android não disponível para mostrar arquivo.', {
      variant: 'warning',
    });
    return;
  }
  try {
    android.showFile(file.file);
  } catch (err) {
    console.error('Erro ao mostrar arquivo:', err);
    enqueueSnackbar(`Erro ao mostrar arquivo: ${err}`, { variant: 'error' });
  }
}

/**
 * Verifica se a versão do Android é a última suportada.
 * @returns {boolean} - Verdadeiro se for a última versão, falso em caso contrário.
 */
export function androidIsLastVersion() {
  if (!android) {
    return false;
  }
  try {
    return android.isLastVersion();
  } catch (err) {
    enqueueSnackbar(`Erro ao verificar versão: ${err}`, { variant: 'error' });
  }
  return false;
}

/**
 * Mostra um Toast no Android.
 * @param {string} message - Mensagem a ser exibida.
 */
export function androidShowToast(message) {
  if (!android) {
    return;
  }
  try {
    android.showToast(message);
  } catch (err) {
    enqueueSnackbar(`Erro ao mostrar Toast: ${err}`, { variant: 'error' });
  }
}

/**
 * Solicita permissao de microfone no app Android.
 */
export function androidRequestMicrophonePermission() {
  if (!android || typeof android.requestMicrophonePermission !== 'function') {
    return;
  }
  try {
    android.requestMicrophonePermission();
  } catch (err) {
    console.error('Erro ao solicitar permissao de microfone no Android:', err);
  }
}

/**
 * Retorna se a permissao de microfone ja foi concedida no app Android.
 * Quando nao estiver no app Android, assume true para nao bloquear navegador.
 */
export function androidHasMicrophonePermission() {
  if (!android || typeof android.hasMicrophonePermission !== 'function') {
    return true;
  }
  try {
    return !!android.hasMicrophonePermission();
  } catch (err) {
    console.error('Erro ao consultar permissao de microfone no Android:', err);
    return false;
  }
}

/**
 * Inicia gravacao nativa no Android.
 * @returns {boolean}
 */
export function androidStartNativeAudioRecording() {
  if (!android || typeof android.startNativeAudioRecording !== 'function') {
    return false;
  }
  try {
    return !!android.startNativeAudioRecording();
  } catch (err) {
    console.error('Erro ao iniciar gravacao nativa no Android:', err);
    return false;
  }
}

/**
 * Finaliza gravacao nativa no Android e devolve payload JSON.
 * @returns {{ok:boolean, base64?:string, mimeType?:string, fileName?:string, error?:string}}
 */
export function androidStopNativeAudioRecording() {
  if (!android || typeof android.stopNativeAudioRecording !== 'function') {
    return { ok: false, error: 'native_not_available' };
  }
  try {
    const raw = `${android.stopNativeAudioRecording() || ''}`;
    if (!raw) return { ok: false, error: 'empty_response' };
    return JSON.parse(raw);
  } catch (err) {
    console.error('Erro ao finalizar gravacao nativa no Android:', err);
    return { ok: false, error: err?.message || 'parse_error' };
  }
}

/**
 * Cancela gravacao nativa no Android.
 */
export function androidCancelNativeAudioRecording() {
  if (!android || typeof android.cancelNativeAudioRecording !== 'function') {
    return;
  }
  try {
    android.cancelNativeAudioRecording();
  } catch (err) {
    console.error('Erro ao cancelar gravacao nativa no Android:', err);
  }
}

/**
 * Indica se a camada nativa esta gravando audio.
 * @returns {boolean}
 */
export function androidIsNativeAudioRecording() {
  if (!android || typeof android.isNativeAudioRecording !== 'function') {
    return false;
  }
  try {
    return !!android.isNativeAudioRecording();
  } catch (err) {
    console.error('Erro ao consultar estado da gravacao nativa:', err);
    return false;
  }
}

/**
 * Retorna numero de serie da VCI (somente digitos) quando disponivel no app Android.
 * @returns {string}
 */
export function androidGetVciSerialNumber() {
  if (!android || typeof android.getVciSerialNumber !== 'function') {
    console.log('[VCI-LINK][FRONT] Bridge Android.getVciSerialNumber indisponivel.');
    return '';
  }
  try {
    const raw = `${android.getVciSerialNumber() || ''}`;
    const digits = raw.replace(/\D/g, '');
    const withoutLeadingZeros = digits.replace(/^0+/, '');
    console.log(
      `[VCI-LINK][FRONT] Bridge Android.getVciSerialNumber raw='${raw}' normalizado='${
        withoutLeadingZeros || digits
      }'`
    );
    return withoutLeadingZeros || digits;
  } catch (err) {
    console.error('Erro ao obter numero de serie da VCI no Android:', err);
    return '';
  }
}

/**
 * Retorna NS do tablet informado pela camada Android (quando disponivel).
 * Ex.: RT9000000012843
 */
export function androidGetTabletSerialNumber() {
  if (!android || typeof android.getTabletSerialNumber !== 'function') {
    console.log('[VCI-LINK][FRONT] Bridge Android.getTabletSerialNumber indisponivel.');
    return '';
  }
  try {
    const serial = `${android.getTabletSerialNumber() || ''}`.trim();
    console.log(`[VCI-LINK][FRONT] Bridge Android.getTabletSerialNumber='${serial}'`);
    return serial;
  } catch (err) {
    console.error('Erro ao obter numero de serie do tablet no Android:', err);
    return '';
  }
}

/**
 * Normaliza NS para fluxo atual (6 digitos), priorizando os ultimos digitos.
 * Ex.: 00132584 -> 132584
 */
export function androidGetVciSerialNumberForCheckout() {
  const serial = androidGetVciSerialNumber();
  if (!serial) return '';
  if (serial.length <= 6) return serial;
  return serial.slice(-6);
}

/**
 * Retorna modelo do dispositivo vindo da camada Android, quando disponivel.
 * Ex.: "rockchip YF_007F"
 */
export function androidGetDeviceModel() {
  if (!android || typeof android.getDeviceModel !== 'function') {
    return '';
  }
  try {
    return `${android.getDeviceModel() || ''}`.trim();
  } catch (err) {
    console.error('Erro ao obter modelo do dispositivo no Android:', err);
    return '';
  }
}

/**
 * Determina se o dispositivo deve operar com audio desabilitado.
 */
export function isAudioDisabledDevice() {
  const modelRaw = androidGetDeviceModel();
  const uaRaw = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

  const normalize = (value) => `${value || ''}`.toLowerCase().replace(/[\s-]+/g, '_');
  const model = normalize(modelRaw);
  const ua = normalize(uaRaw);

  // Detecta variacoes comuns sem depender de um formato unico vindo do bridge.
  const compactModel = model.replace(/[^a-z0-9]/g, '');
  const compactUa = ua.replace(/[^a-z0-9]/g, '');

  const hasRockchipToken =
    model.includes('rockchip') ||
    compactModel.includes('rockchip') ||
    ua.includes('rockchip') ||
    compactUa.includes('rockchip');

  const hasYf007fToken =
    model.includes('yf_007f') ||
    model.includes('yf007f') ||
    compactModel.includes('yf007f') ||
    ua.includes('yf_007f') ||
    ua.includes('yf007f') ||
    compactUa.includes('yf007f');

  // Em alguns tablets o bridge nao traz "rockchip", mas o modelo YF_007F vem corretamente.
  const disabled = hasYf007fToken && (hasRockchipToken || model.includes('yf_007f'));

  console.log(
    `[AUDIO-DISABLE][FRONT] modelRaw='${modelRaw}' ua='${uaRaw}' normalized='${model}' disabled=${disabled}`
  );
  return disabled;
}
